import { Tooltip } from '@heroui/react';
import { ChevronDownIcon } from '@/components/icons';
import { useLongPress } from '@/lib/useLongPress';
import { TooltipTarget } from '@/components/ui/TooltipTarget';

/**
 * An EXPANDED server folder in the vertical rail: the folder's name as a
 * caption, a spine in the folder colour running down the group, and a hairline
 * closing it off.
 *
 * It replaces a tinted rounded backdrop around the whole group. That backdrop
 * was wider than the avatars it held, so an expanded folder broke the column's
 * rhythm — the rail stopped reading as one stack of servers and became a blob
 * with servers in it. A caption plus a 2px spine says the same thing (these
 * belong together, this is their colour, here is where they end) without
 * competing with the icons.
 *
 * The caption row is also the collapse toggle, the drop target and the
 * context-menu anchor, because when expanded there is no head tile to carry
 * them.
 */
export function FolderShelf(props: {
  /** Folder name, or null for an unnamed folder — see `railFolderCaption`. */
  caption: string | null;
  /** The folder's colour: `folder.color` or the app accent. */
  tint: string;
  /** Aria label for the toggle (folders without a caption need one anyway). */
  label: string;
  isDropTarget: boolean;
  onToggle: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onContextMenuAt?: (x: number, y: number) => void;
  dropProps?: React.HTMLAttributes<HTMLButtonElement> & { 'data-folder-drop'?: string };
  children: React.ReactNode;
}) {
  const longPress = useLongPress((x, y) => props.onContextMenuAt?.(x, y));
  return (
    <div
      className={[
        'flex w-full shrink-0 flex-col rounded-[6px]',
        props.isDropTarget ? 'ring-2 ring-primary ring-offset-2 ring-offset-surface' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Tooltip delay={0}>
        <TooltipTarget>
          <button
            aria-label={props.label}
            type="button"
            onClick={props.onToggle}
            onContextMenu={props.onContextMenu}
            aria-expanded
            {...props.dropProps}
            {...longPress}
            className="group/cap flex w-full items-center justify-center gap-1 px-1.5 pb-1 text-muted [-webkit-touch-callout:none]"
          >
            {props.caption ? (
              <span
                // Micro-caps, clamped: the rail is 76px wide, so a long folder
                // name ends in an ellipsis and lives in the tooltip. No chevron
                // beside it — it cost a third of the available characters, and
                // an open folder is already obvious from the servers below it.
                className="truncate text-[10px] font-semibold uppercase tracking-[0.08em]"
                // Mixed halfway into the muted ink rather than painted in the
                // folder colour flat: a saturated hue at 10px on a light
                // parchment ground measured 4.46:1, under the 4.5 floor.
                style={{ color: `color-mix(in srgb, ${props.tint} 50%, currentColor)` }}
              >
                {props.caption}
              </span>
            ) : (
              // Unnamed folder: nothing to print, so the chevron carries the
              // collapse affordance on its own.
              <ChevronDownIcon
                className="size-3 shrink-0 opacity-70 transition-opacity group-hover/cap:opacity-100"
                style={{ color: props.tint }}
              />
            )}
          </button>
        </TooltipTarget>
        <Tooltip.Content showArrow placement="right">
          <Tooltip.Arrow />
          {props.label}
        </Tooltip.Content>
      </Tooltip>
      {/* pb: the group needs to end somewhere. A hairline was the obvious way,
          but the cispracs skin is deliberately borderless — theme.ts zeroes
          --separator/--border/--divider — so a rule here would be the only line
          in the shell. The spine stopping plus a little air does the same job in
          the skin's own language. */}
      <div className="relative flex flex-col items-center gap-2.5 pb-1.5">
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-1.5 left-1.5 top-0 w-0.5 rounded-full opacity-80"
          style={{ backgroundColor: props.tint }}
        />
        {props.children}
      </div>
    </div>
  );
}
